import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  Alert
} from '@mui/material';
import ConditionBuilder from './ConditionBuilder';
import ActionBuilder from './ActionBuilder';
import { validateRuleStructure } from '../../../utils/transformations/ruleTransformations';

const RuleEditorDialog = ({ open, rule, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    priority: 1,
    name: '',
    description: '',
    isActive: true,
    conditions: {
      operator: 'single',
      criteria: {
        type: 'flag_present',
        domain: 'depression',
        operator: '==',  // Required by Mongoose schema
        value: true
      }
    },
    action: {
      type: 'assign_persona',
      personaKey: '',
      reason: ''
    }
  });

  const [validation, setValidation] = useState({ isValid: true, errors: [] });

  useEffect(() => {
    if (rule) {
      setFormData({
        priority: rule.priority,
        name: rule.name,
        description: rule.description || '',
        isActive: rule.isActive !== false,
        conditions: rule.conditions,
        action: rule.action
      });
    } else {
      // Reset for new rule
      setFormData({
        priority: 1,
        name: '',
        description: '',
        isActive: true,
        conditions: {
          operator: 'single',
          criteria: {
            type: 'flag_present',
            domain: 'depression',
            value: true
          }
        },
        action: {
          type: 'assign_persona',
          personaKey: '',
          reason: ''
        }
      });
    }
    setValidation({ isValid: true, errors: [] });
  }, [rule, open]);

  const handleChange = (field, value) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);

    // Validate
    const validationResult = validateRuleStructure(updated);
    setValidation(validationResult);
  };

  const handleSave = () => {
    const validationResult = validateRuleStructure(formData);
    if (!validationResult.isValid) {
      setValidation(validationResult);
      return;
    }

    onSave(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {rule ? 'Edit Rule' : 'Create New Rule'}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <TextField
            fullWidth
            label="Rule Name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            sx={{ mb: 2 }}
            required
            error={!formData.name}
            helperText={!formData.name ? 'Name is required' : ''}
          />

          <TextField
            fullWidth
            multiline
            rows={2}
            label="Description"
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            type="number"
            label="Priority"
            value={formData.priority}
            onChange={(e) => handleChange('priority', Number(e.target.value))}
            sx={{ mb: 2 }}
            required
            helperText="Lower numbers = higher priority (evaluated first)"
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" gutterBottom>
            Conditions
          </Typography>
          <ConditionBuilder
            conditions={formData.conditions}
            onChange={(conditions) => handleChange('conditions', conditions)}
          />

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" gutterBottom>
            Action
          </Typography>
          <ActionBuilder
            action={formData.action}
            onChange={(action) => handleChange('action', action)}
          />

          {!validation.isValid && validation.errors.length > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Validation Errors:</Typography>
              {validation.errors.map((error, idx) => (
                <Typography key={idx} variant="caption" display="block">
                  - {error}
                </Typography>
              ))}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={!validation.isValid}
        >
          {rule ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RuleEditorDialog;
